// src/pages/lessons/ReviewsPage.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../AuthUser";
import API from "../../../api";
import moment from "moment";
import {
    Badge,
    Button,
    Card,
    Col,
    Form,
    Row,
    Table,
    Dropdown,
} from "react-bootstrap";
import LoadingDiv from "../../../components/LoadingDiv";
import TextWithLinks from "../../../components/TextWithLinks";
import EditLessonModal from "../components/EditReviewModal";
import { toast } from "react-toastify";

const PAGE_SIZE = 10;
const FETCH_SIZE = 25; // rows per server request (API caps limit at 100)

// Accept both response shapes: the paginated { rows, total, counts } and the
// legacy plain array (an api that predates limit/offset support, or a
// rollback). With the legacy shape total === rows.length, so the pager
// simply behaves like the old client-side one.
function normalizeReviewsPayload(payload) {
    if (Array.isArray(payload)) {
        const complete = payload.filter((l) => l.status === "complete").length;
        return {
            rows: payload,
            total: payload.length,
            counts: { complete, pending: payload.length - complete },
        };
    }
    return {
        rows: payload?.rows || [],
        total: payload?.total || 0,
        counts: payload?.counts || { complete: 0, pending: 0 },
    };
}

function statusBadge(status) {
    if (status === "complete") return <Badge bg="success">Completed</Badge>;
    return <Badge bg="danger">Pending</Badge>;
}

function formatDate(d) {
    if (!d) return "—";
    return moment.utc(d, "YYYY-MM-DD").format("ll");
}

// Prefer instructor review in reviewVideos[type===\"instructor\"].
// Some payloads call the url field `filename`; include as fallback.
function getDownloadUrl(l) {
    const instructorItem = l?.reviewVideos?.find?.((v) => v?.type === "instructor");
    return (
        instructorItem?.downloadUrl ||
        instructorItem?.url ||
        instructorItem?.filename ||
        l?.downloadUrl ||
        l?.videoUrl ||
        null
    );
}

/*
// --- Instructor helpers (commented out per request) ---
function getInstructorObj(l) {
  return l?.instructor || l?.assignedTo || l?.coach || null;
}
function getInstructorName(l) {
  if (typeof l?.instructorName === "string" && l.instructorName.trim()) return l.instructorName;
  const ins = getInstructorObj(l);
  if (!ins) return null;
  if (ins.firstName || ins.lastName) return [ins.firstName, ins.lastName].filter(Boolean).join(" ");
  if (typeof ins.name === "string") return ins.name;
  if (typeof ins.email === "string") return ins.email;
  return null;
}
function getInstructorAvatar(l) {
  const ins = getInstructorObj(l);
  return ins?.thumbnail || ins?.avatar || null;
}
*/

export default function ReviewsPage() {
    const auth = useAuth();
    const navigate = useNavigate();

    const [lessons, setLessons] = useState([]);
    const [total, setTotal] = useState(0);
    const [srvCounts, setSrvCounts] = useState({ complete: 0, pending: 0 });
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("all"); // all | pending | completed

    const [editing, setEditing] = useState(null); // lesson obj or null
    const [openMenuId, setOpenMenuId] = useState(null); // controls all dropdowns
    const [account, setAccount] = useState(null); // for remaining count
    const [expandedNotes, setExpandedNotes] = useState(() => new Set()); // mobile show more/less

    useEffect(() => {
        let mounted = true;
        setLoading(true);

        // Parallel fetches. With limit/offset the API returns the paginated
        // shape { rows, total, counts } instead of the legacy plain array.
        Promise.all([
            API.get("reviews", { params: { limit: FETCH_SIZE, offset: 0 } }),
            API.get("accounts/me"),
        ])
            .then(([reviewsRes, acctRes]) => {
                if (!mounted) return;
                const data = normalizeReviewsPayload(reviewsRes?.data);
                setLessons(data.rows);
                setTotal(data.total);
                setSrvCounts(data.counts);
                setAccount(acctRes?.data || null);
            })
            .finally(() => mounted && setLoading(false));

        return () => (mounted = false);
    }, []);

    const forceDownload = async (url, filename = "review.mp4") => {
        try {
            const res = await fetch(url);
            // A dead S3 object answers with an XML error body — without this
            // check it would get saved as a broken .mp4.
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();

            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (e) {
            toast.error("This video is no longer available.");
        }
    };

    // Status chips reflect the whole history (server-side counts), not just
    // the rows loaded so far.
    const counts = {
        all: total,
        pending: srvCounts.pending,
        completed: srvCounts.complete,
    };

    // Derived data
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return lessons.filter((l) => {
            if (statusFilter === "pending" && l.status !== "pending") return false;
            if (statusFilter === "completed" && l.status !== "complete") return false;

            if (!q) return true;
            const hay =
                [
                    l.status,
                    l.notes,
                    formatDate(l.created_at),
                    formatDate(l.updated_at),
                    String(l.id),
                ]
                    .join(" ")
                    .toLowerCase() || "";
            return hay.includes(q);
        });
    }, [lessons, query, statusFilter]);

    // Unfiltered page count comes from the server total, so the pager shows
    // the full history even before all rows are loaded. With a search/status
    // filter active we can only page over what's loaded so far.
    const isFiltering = query.trim() !== "" || statusFilter !== "all";
    const totalPages = Math.max(
        1,
        Math.ceil((isFiltering ? filtered.length : total) / PAGE_SIZE)
    );
    const pageClamped = Math.min(page, totalPages);
    const paged = useMemo(() => {
        const start = (pageClamped - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, pageClamped]);

    const hasMore = lessons.length < total;

    const loadMore = useCallback(async () => {
        if (loadingMore) return;
        setLoadingMore(true);
        try {
            const res = await API.get("reviews", {
                params: { limit: FETCH_SIZE, offset: lessons.length },
            });
            const data = normalizeReviewsPayload(res?.data);
            // Dedupe by id: a review submitted between fetches shifts the
            // offset and would otherwise duplicate a row.
            setLessons((prev) => {
                const seen = new Set(prev.map((l) => l.id));
                return [...prev, ...data.rows.filter((r) => !seen.has(r.id))];
            });
            setTotal(data.total);
            setSrvCounts(data.counts);
        } finally {
            setLoadingMore(false);
        }
    }, [lessons.length, loadingMore]);

    const scrollToTop = useCallback(() => {
        window.requestAnimationFrame(() =>
            window.scrollTo({ top: 0, behavior: "smooth" })
        );
    }, []);

    const goPrev = () => {
        setPage((p) => Math.max(1, p - 1));
        scrollToTop();
        setOpenMenuId(null);
    };

    const goNext = async () => {
        const nextPage = pageClamped + 1;
        // Lazy loading: pull the next batch before stepping onto a page the
        // loaded rows don't cover yet. While filtering, the last loaded page
        // may hide older matches — fetch more so they can surface.
        const needsRows = isFiltering
            ? pageClamped >= totalPages
            : lessons.length < Math.min(nextPage * PAGE_SIZE, total);
        if (hasMore && needsRows) {
            await loadMore();
        }
        setPage(nextPage);
        scrollToTop();
        setOpenMenuId(null);
    };

    const toggleExpand = (id) => {
        setExpandedNotes((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Handlers
    const goView = (lesson) => {
        if (lesson.status === "complete") {
            navigate(`/lesson/${lesson.id}`);
        } else {
            setEditing(lesson);
        }
    };

    const onUpdated = (updatedLesson) => {
        setLessons((prev) =>
            prev.map((l) => (l.id === updatedLesson.id ? updatedLesson : l))
        );
        setEditing(null);
    };

    const onDeleted = async (id) => {
        setLessons((prev) => prev.filter((l) => l.id !== id));
        // Only pending lessons are deletable from this page.
        setTotal((t) => Math.max(0, t - 1));
        setSrvCounts((c) => ({ ...c, pending: Math.max(0, c.pending - 1) }));
        setEditing(null);
    };

    const deleteDirect = async (lesson) => {
        if (!window.confirm("Delete this lesson? This cannot be undone.")) return;
        try {
            await API.delete(`reviews/${lesson.id}`);
            toast.success("Successfully deleted lesson");
            onDeleted(lesson.id);
        } catch (e) {
            toast.error("Could not delete lesson");
        }
    };

    // Close menus when clicking outside
    useEffect(() => {
        const close = () => setOpenMenuId(null);
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, []);

    // Remaining lesson label (Unlimited if >= 10, matches your existing rule)
    const remainingText = (() => {
        const remaining = account?.lessonCountRemaining ?? auth?.user?.lessonCountRemaining;
        if (remaining == null) return null;
        return remaining >= 10 ? "Unlimited Lesson(s) Left" : `${remaining} Lesson(s) Left`;
    })();

    // ---- InlineSortDropdown (same pattern as video list; lives inside to use dynamic labels) ----
    const SORT_LABELS = {
        all: `All Reviews (${counts.all})`,
        pending: `Pending (${counts.pending})`,
        completed: `Completed (${counts.completed})`,
    };

    function InlineSortDropdown({ value, onChange, className = "sort-inline d-lg-none" }) {
        const [open, setOpen] = useState(false);
        const ref = useRef(null);

        const label = SORT_LABELS[value] || "All Reviews";

        useEffect(() => {
            const onDoc = (e) => {
                if (!ref.current) return;
                if (e.key === "Escape") setOpen(false);
                if (!ref.current.contains(e.target)) setOpen(false);
            };
            document.addEventListener("mousedown", onDoc);
            document.addEventListener("keydown", onDoc);
            return () => {
                document.removeEventListener("mousedown", onDoc);
                document.removeEventListener("keydown", onDoc);
            };
        }, []);

        return (
            <div className={className} ref={ref}>
                <button type="button" className="sort-inline-toggle" onClick={() => setOpen((o) => !o)}>
                    {label}
                </button>
                <ul className="sort-menu" hidden={!open}>
                    {Object.entries(SORT_LABELS).map(([key, text]) => (
                        <li key={key}>
                            <button
                                className={`sort-item ${value === key ? "active" : ""}`}
                                onClick={() => {
                                    onChange(key);
                                    setOpen(false);
                                }}
                            >
                                {text}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }
    // --------------------------------------------------------------------------------------------

    // Shared pager (desktop table + mobile list). "of N+" hints that more
    // rows may match the active filter once further batches are loaded.
    const pager = (totalPages > 1 || (isFiltering && hasMore)) && (
        <div className="d-flex justify-content-between align-items-center mt-3 px-3 pb-2">
            <Button size="sm" variant="light" disabled={pageClamped <= 1} onClick={goPrev}>
                ‹ Prev
            </Button>
            <div className="small text-muted">
                Page {pageClamped} of {totalPages}
                {isFiltering && hasMore ? "+" : ""}
            </div>
            <Button
                size="sm"
                variant="light"
                disabled={loadingMore || (pageClamped >= totalPages && !hasMore)}
                onClick={goNext}
            >
                {loadingMore ? "Loading…" : "Next ›"}
            </Button>
        </div>
    );

    return (
        <div className="container px-0 px-md-3 py-3">
            {/* Utility styles */}
            <style>{`
        .no-caret::after { display: none !important; }
        .notes-ellipsis {
          max-width: 420px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .reviews-search { max-width: 320px; }
        }
        @media (max-width: 767.98px) {
          .reviews-header { gap: .5rem; }
          .reviews-header .reviews-search { flex: 1 1 auto; }
          .reviews-header .submit-btn { white-space: nowrap; }
          /* Remove any card chrome on mobile */
          .mobile-plain { box-shadow: none !important; border: 0 !important; background: transparent !important; }
        }

        /* Minimal styles for inline sort dropdown (reuse from video list) */
        .sort-inline { position: relative; }
        .sort-inline .sort-inline-toggle {
          border: 1px solid rgba(0,0,0,.125);
          background: #fff;
          border-radius: .375rem;
          padding: .25rem .5rem;
          font-size: .875rem;
        }
        .sort-inline .sort-menu {
          position: absolute;
          z-index: 1030;
          margin: .25rem 0 0;
          padding: .25rem 0;
          list-style: none;
          background: #fff;
          border: 1px solid rgba(0,0,0,.15);
          border-radius: .375rem;
          min-width: 12rem;
          box-shadow: 0 .5rem 1rem rgba(0,0,0,.15);
        }
        .sort-inline .sort-item {
          display: block;
          width: 100%;
          text-align: left;
          padding: .375rem .75rem;
          background: transparent;
          border: 0;
        }
        .sort-inline .sort-item.active {
          font-weight: 600;
        }

        /* ===== Mobile full-width list ===== */
        @media (max-width: 767.98px) {
          .mobile-list-wrap { background:#fff; }
          .review-list-row {
            display:flex;
            align-items:flex-start;
            gap:10px;
            padding: 18px 16px 18px 14px;
            border-bottom: 1px solid rgba(0,0,0,0.08);
          }
          @media (max-width: 767.98px) {
  /* Remove native border completely */
  .review-list-row {
    border: none !important;
    position: relative;
  }
/* Hover (desktop / pointer devices) */
  .review-list-row:hover {
    background: #f7f7f9; /* subtle light highlight */
  }

  /* Active press (mobile tap feedback) */
  .review-list-row:active {
    background: #efeff3; /* slightly stronger */
  }
  .review-list-row {
  transition: background 0.18s ease;
}
  /* Inset bottom divider (for every row except the last one) */
  .review-list-row::after {
    content: "";
    position: absolute;
    left: 16px;   /* match your row's horizontal padding */
    right: 16px;  /* match your row's horizontal padding */
    bottom: 0;
    height: 1px;
    background: rgba(0,0,0,0.08);
  }

  /* Remove top border for the first row */
  .review-list-row:first-child::before {
    content: none !important;
  }

  /* Remove bottom divider on last row (clean ending) */
  .review-list-row:last-child::after {
    content: none !important;
  }
}

          .review-list-row:first-child {
            border-top: 1px solid rgba(0,0,0,0.08);
          }
          .review-left { flex:1 1 auto; min-width:0; }
          .review-title { line-height:1; }
          .review-meta { line-height:1.2; }
          .review-notes { color:#333; }
          .review-actions { display:flex; align-items:center; gap:6px; margin-left:6px; }
          .download-circle {
            height: 34px; width: 34px; border-radius: 50%;
            background: #f1f1f4; border: 0;
            display:flex; align-items:center; justify-content:center;
          }
          .download-circle i { font-size:1.1rem; }
          /* Dropdown toggle is plain icon (no circle) */
          .plain-icon-btn {
            border:0; background:transparent; padding:0;
            display:flex; align-items:center; justify-content:center;
            height:34px; width:34px;
          }
        }
       
      `}</style>

            {/* Header */}
            <div className=" container mb-3">
                <h1 className="h4 mb-2">Swing Reviews</h1>

                {/* First row: search + submit (mobile same line) */}
                <div className="reviews-header d-flex align-items-center">
                    <Form onSubmit={(e) => e.preventDefault()} className="flex-grow-1 d-flex">
                        <Form.Control
                            size="sm"
                            type="search"
                            className="reviews-search"
                            placeholder="Search status, date, notes…"
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setPage(1);
                            }}
                        />
                    </Form>

                    <div className="ms-2 d-flex align-items-center gap-2">
                        <Button
                            size="sm"
                            variant="success"
                            className="submit-btn"
                            onClick={() => navigate("/upload-videos")}
                            disabled={!auth?.user?.canSubmitLesson}
                        >
                            Submit Swing
                        </Button>
                    </div>
                </div>

                {/* Second row: status filter (left) + remaining count (desktop) */}
                <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
                    {/* Inline sort-style dropdown (left) */}
                    <InlineSortDropdown
                        value={statusFilter}
                        onChange={(key) => {
                            setStatusFilter(key);
                            setPage(1);
                        }}
                        className="sort-inline"
                    />

                    {/* Desktop remaining text on the right */}
                    {remainingText && (
                        <div className="text-muted small d-none d-md-block">{remainingText}</div>
                    )}
                </div>
            </div>

            {/* Desktop in a Card; Mobile list is full-bleed (container px-0) */}
            <Card className="border-0 shadow-sm mobile-plain d-none d-md-block">
                <Card.Body>
                    <Row>
                        <Col xs={12}>
                            {loading ? (
                                <LoadingDiv />
                            ) : !lessons.length ? (
                                // Empty states based on ability to submit
                                !auth?.user?.canSubmitLesson ? (
                                    <div className="text-center p-4">
                                        <h5 className="mb-2">Unlock Swing Reviews</h5>
                                        <p className="text-muted mb-3">
                                            Get your swing analyzed twice a month for less than the cost of two dozen golf balls.
                                        </p>
                                        <Button variant="success" size="sm" onClick={() => navigate("/pricing")}>
                                            Get Access Now
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="text-center p-4">
                                        <h5 className="mb-2">No reviews yet</h5>
                                        <p className="text-muted mb-3">
                                            You haven’t submitted any swing reviews yet.
                                        </p>
                                        <div className="d-flex justify-content-center gap-2">
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={() => navigate("/upload-videos")}
                                            >
                                                Submit Swing
                                            </Button>
                                            {remainingText && (
                                                <span className="text-muted small align-self-center">{remainingText}</span>
                                            )}
                                        </div>
                                    </div>
                                )
                            ) : (
                                <>
                                    {/* Desktop table */}
                                    <div className="d-none d-md-block">
                                        <Table hover className="align-middle">
                                            <thead>
                                            <tr>
                                                <th style={{ width: 120 }}>Status</th>
                                                {/* <th style={{ width: 220 }}>Instructor</th> */}
                                                <th style={{ width: 150 }}>Submitted</th>
                                                <th style={{ width: 150 }}>Updated</th>
                                                <th>Notes</th>
                                                <th style={{ width: 48 }} className="text-end"></th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {paged.map((l) => (
                                                <tr
                                                    key={l.id}
                                                    onClick={() => goView(l)}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <td>{statusBadge(l.status)}</td>
                                                    <td>{formatDate(l.created_at)}</td>
                                                    <td>{formatDate(l.updated_at)}</td>
                                                    <td className="notes-ellipsis">
                                                        {l.notes ? <TextWithLinks text={l.notes}/> : <span className="text-muted">—</span>}
                                                    </td>
                                                    <td className="text-end">
                                                        <Dropdown
                                                            align="end"
                                                            show={openMenuId === l.id}
                                                            onToggle={(isOpen, e) => {
                                                                e?.stopPropagation?.();
                                                                setOpenMenuId(isOpen ? l.id : null);
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <Dropdown.Toggle
                                                                variant="link"
                                                                className="no-caret p-0 text-muted"
                                                            >
                                                                <i className="bi bi-three-dots-vertical" style={{ fontSize: "1.25rem" }} />
                                                            </Dropdown.Toggle>

                                                            <Dropdown.Menu>
                                                                {l.status === "complete" ? (
                                                                    <>
                                                                        <Dropdown.Item onClick={() => navigate(`/lesson/${l.id}`)}>
                                                                            View Review
                                                                        </Dropdown.Item>
                                                                        {getDownloadUrl(l) && (
                                                                            <Dropdown.Item
                                                                                onClick={() =>
                                                                                    forceDownload(getDownloadUrl(l), `Swing-Review-${l.id}.mp4`)
                                                                                }
                                                                            >
                                                                                Download Video
                                                                            </Dropdown.Item>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Dropdown.Item onClick={() => setEditing(l)}>
                                                                            Edit Notes
                                                                        </Dropdown.Item>
                                                                        <Dropdown.Item
                                                                            className="text-danger"
                                                                            onClick={() => deleteDirect(l)}
                                                                        >
                                                                            Delete Lesson
                                                                        </Dropdown.Item>
                                                                    </>
                                                                )}
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </Table>
                                        {pager}
                                    </div>
                                </>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* MOBILE: full-width list view (borders touch edges; no left/right padding) */}
            <div className="d-md-none mobile-list-wrap">
                {loading ? (
                    <LoadingDiv />
                ) : !lessons.length ? (
                    !auth?.user?.canSubmitLesson ? (
                        <div className="text-center p-4">
                            <h5 className="mb-2">Unlock Swing Reviews</h5>
                            <p className="text-muted mb-3">
                                Get your swing analyzed twice a month for less than the cost of two dozen golf balls.
                            </p>
                            <Button variant="success" size="sm" onClick={() => navigate("/pricing")}>
                                Get Access Now
                            </Button>
                        </div>
                    ) : (
                        <div className="text-center p-4">
                            <h5 className="mb-2">No reviews yet</h5>
                            <p className="text-muted mb-3">
                                You haven’t submitted any swing reviews yet.
                            </p>
                            <div className="d-flex justify-content-center gap-2">
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => navigate("/upload-videos")}
                                >
                                    Submit Swing
                                </Button>
                                {remainingText && (
                                    <span className="text-muted small align-self-center">{remainingText}</span>
                                )}
                            </div>
                        </div>
                    )
                ) : (
                    <>
                        {paged.map((l) => {
                            const expanded = expandedNotes.has(l.id);
                            const shouldShowToggle = (l.notes || "").length > 120;
                            const downloadUrl = getDownloadUrl(l);
                            return (
                                <div
                                    key={l.id}
                                    className="review-list-row"
                                    onClick={() => goView(l)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="review-left">
                                        <div className="review-title">{statusBadge(l.status)}</div>
                                        <div className="review-meta small text-muted mt-1">
                                            Submitted {formatDate(l.created_at)}
                                            {l.updated_at ? ` • Updated ${formatDate(l.updated_at)}` : ""}
                                        </div>
                                        {l.notes && (
                                            <div className="review-notes small mt-1">
                                                <span className={!expanded ? "line-clamp-2" : ""}><TextWithLinks text={l.notes}/></span>
                                                {shouldShowToggle && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm p-0 ms-1 align-baseline"
                                                        onClick={(e) => { e.stopPropagation(); toggleExpand(l.id); }}
                                                    >
                                                        {expanded ? "Show less" : "Show more"}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="review-actions">
                                        {l.status === "complete" && downloadUrl && (
                                            <button
                                                className="download-circle"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    forceDownload(downloadUrl, `Swing-Review-${l.id}.mp4`);
                                                }}
                                                aria-label="Download review video"
                                            >
                                                <i className="bi bi-cloud-arrow-down"></i>
                                            </button>
                                        )}

                                        <Dropdown
                                            align="end"
                                            show={openMenuId === l.id}
                                            onToggle={(isOpen, e) => {
                                                e?.stopPropagation?.();
                                                setOpenMenuId(isOpen ? l.id : null);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Dropdown.Toggle
                                                variant="link"
                                                className="no-caret plain-icon-btn text-muted"
                                            >
                                                <i className="bi bi-three-dots-vertical"></i>
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu>
                                                {l.status === "complete" ? (
                                                    <>
                                                        <Dropdown.Item onClick={() => navigate(`/lesson/${l.id}`)}>
                                                            View Review
                                                        </Dropdown.Item>
                                                        {downloadUrl && (
                                                            <Dropdown.Item
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    forceDownload(downloadUrl, `Swing-Review-${l.id}.mp4`);
                                                                }}
                                                            >
                                                                Download Video
                                                            </Dropdown.Item>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Dropdown.Item onClick={() => setEditing(l)}>
                                                            Edit Notes
                                                        </Dropdown.Item>
                                                        <Dropdown.Item className="text-danger" onClick={() => deleteDirect(l)}>
                                                            Delete Lesson
                                                        </Dropdown.Item>
                                                    </>
                                                )}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}

                {/* Pagination (mobile shares same controls) */}
                {pager}
            </div>

            {/* Modal for pending lesson edit/delete */}
            {editing && (
                <EditLessonModal
                    lesson={editing}
                    onClose={() => setEditing(null)}
                    onUpdated={onUpdated}
                    onDeleted={onDeleted}
                />
            )}
        </div>
    );
}
